import { ChangeDetectionStrategy, Component, inject, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DsBoxModule,
  DsButtonModule,
  DsCheckboxModule,
  DsLabelModule,
  DsMessageModule,
  DsTooltipDirectiveModule,
} from '@bmw-ds/components';

import { LegalEntitiesService } from '../../../customer/clients/retail-crm/legal-entities.service';
import { RetailCrmRelatedDealer } from '../../../customer/clients/retail-crm/model/responses/legal-entities.model';
import { useI18n } from '../../../i18n/services/i18n.service';
import { ConsentCheckboxGroup, ConsentSelection, ConsentType } from '../../model/consent-checkbox';
import { ConsentLegalText } from '../../model/nsc-consent-legal-text';
import { ConsentProvider } from '../../services/consent.provider';
import { DealerConsentVisibilityService } from '../../services/dealer-consent-visibility.service';
import { ConsentBoxComponent } from './consent-box.component';

@Component({
  selector: 'cdf-consent-box-container',
  imports: [
    DsBoxModule,
    DsButtonModule,
    DsTooltipDirectiveModule,
    DsMessageModule,
    DsCheckboxModule,
    FormsModule,
    DsLabelModule,
    ConsentBoxComponent,
  ],
  template: `
    <cdf-consent-box
      [id]="consentType() + '-CONSENT'"
      [(consentSelection)]="consentSelection"
      [consentSelectionError]="consentSelectionError()"
      [relatedDealers]="legalEntitiesService.sortedRelatedDealers()"
      [dealerLegalEntitiesSelection]="dealerLegalEntitiesSelection()"
      (dealerLegalEntitiesSelectionChange)="dealerLegalEntitiesSelectionChange.emit($event)"
      [dealerMandatoryLegalEntities]="dealerMandatoryLegalEntities()"
      [customerLanguage]="customerLanguage()"
      [consentCheckboxGroups]="consentCheckboxGroups()"
      [legalText]="legalText()"
      [canSave]="canSave()"
      [isSaving]="isSaving()"
      [onlyImplicitConsentsSelected]="onlyImplicitConsentsSelected()"
      [initialConsentCreationMode]="initialConsentCreationMode()"
      [missingRegistration]="missingRegistration()"
      [consentType]="consentType()"
      [consentSaveDate]="consentSaveDate()"
      [consentMetadata]="consentProvider.metadata()"
      [readonly]="readonly()"
      [showCopyNscToDealerConsents]="dealerConsentVisibilityService.showDealerConsent()"
      (confirm)="confirm.emit()"
      (copyConsent)="copyConsent.emit()"
      (updateLegalEntities)="updateLegalEntities($event.checked, $event.chosenDealers)"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsentBoxContainer {
  readonly consentSelection = model.required<ConsentSelection | undefined>();
  readonly consentSelectionError = input.required<boolean>();
  readonly dealerConsentVisibilityService = inject(DealerConsentVisibilityService);
  readonly dealerLegalEntitiesSelectionChange = output<string[] | undefined>();
  readonly dealerMandatoryLegalEntities = input<string[]>([]);
  readonly customerLanguage = input.required<string | undefined>();
  readonly consentCheckboxGroups = input.required<ConsentCheckboxGroup[] | undefined>();
  readonly legalText = input.required<ConsentLegalText>();
  readonly canSave = input(true);
  readonly isSaving = input(false);
  readonly onlyImplicitConsentsSelected = input(false);
  readonly initialConsentCreationMode = input(false);
  readonly missingRegistration = input(false);
  readonly consentType = input<ConsentType>(ConsentType.NSC);
  readonly consentSaveDate = input<Date>();
  readonly readonly = input(false);

  readonly confirm = output();
  readonly copyConsent = output();

  protected readonly i18n = useI18n();
  protected readonly consentProvider = inject(ConsentProvider);
  protected readonly legalEntitiesService = inject(LegalEntitiesService);

  protected readonly dealerLegalEntitiesSelection = signal(this.dealerMandatoryLegalEntities());

  updateLegalEntities(checked: boolean, chosenDealers: RetailCrmRelatedDealer[]) {
    const existingSelection = this.dealerLegalEntitiesSelection() ?? [];
    const updatedValues = chosenDealers.map((dealer) => dealer.distributionPartnerNr);

    if (checked) {
      this.dealerLegalEntitiesSelection.set(merge(existingSelection, updatedValues));
    } else {
      this.dealerLegalEntitiesSelection.set(difference(existingSelection, updatedValues));
    }

    if (this.dealerLegalEntitiesSelection().length === 0) {
      this.dealerLegalEntitiesSelectionChange.emit(undefined);
    } else {
      this.dealerLegalEntitiesSelectionChange.emit(
        merge(this.dealerMandatoryLegalEntities(), this.dealerLegalEntitiesSelection()),
      );
    }
  }
}

function merge(a: string[], b: string[]): string[] {
  return Array.from(new Set([...a, ...b]));
}

function difference(a: string[], b: string[]): string[] {
  return a.filter((item) => !b.includes(item));
}
